import { addDoc, collection, getDocs, limit, query } from 'firebase/firestore'
import { db } from './config'
import { getTuhiUserId } from 'app/store/slices/authSlice'

const OFFLINE_LOGS_KEY = 'offline-logs'

/**
 * Structure of a log entry
 */
interface LogData {
    type: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG'
    event: string
    message: string
    userId: string
    meta: Record<string, unknown>
    timestamp: string
}

// Quick check if Firebase is reachable (with timeout)
const isFirebaseOnline = async (): Promise<boolean> => {
    try {
        const q = query(collection(db, 'logs'), limit(1))
        await getDocs(q)
        return true
    } catch {
        return false
    }
}

function serializeError(
    error: unknown
): { name: string; message: string; stack?: string } | { message: string } {
    if (error instanceof Error) {
        const serialized: { name: string; message: string; stack?: string } = {
            name: error.name,
            message: error.message,
        }
        // Only include `stack` when present — some errors (e.g. a browser
        // NotAllowedError) have `stack === undefined`, and Firestore's addDoc()
        // rejects undefined field values.
        if (error.stack !== undefined) {
            serialized.stack = error.stack
        }
        return serialized
    }
    return { message: String(error) }
}

// Recursively sanitize meta object (convert Errors into plain objects)
function sanitizeMeta(meta: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(meta)) {
        if (value === undefined) {
            // Firestore rejects `undefined` field values; drop the key entirely.
            continue
        }
        if (value instanceof Error) {
            sanitized[key] = serializeError(value)
        } else if (
            typeof value === 'object' &&
            value !== null &&
            !Array.isArray(value)
        ) {
            sanitized[key] = sanitizeMeta(value as Record<string, unknown>)
        } else {
            sanitized[key] = value
        }
    }

    return sanitized
}

export const logEvent = async (
    type: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG',
    event: string,
    message: string,
    meta?: Record<string, unknown>
) => {
    const userId = getTuhiUserId() ?? ''

    const logData: LogData = {
        type,
        event,
        message,
        userId,
        meta: meta ? sanitizeMeta(meta) : {},
        timestamp: new Date().toISOString(), // keep original log creation time
    }

    const online = await isFirebaseOnline()

    if (!online) {
        console.warn('Firebase unreachable, storing log locally.')
        storeOfflineLog(logData)
        return
    }

    try {
        await addDoc(collection(db, 'logs'), logData)
    } catch (err) {
        console.error('Failed to log to Firebase:', err)
        storeOfflineLog(logData)
    }
}

const storeOfflineLog = (logData: LogData) => {
    const existingLogs = JSON.parse(
        localStorage.getItem(OFFLINE_LOGS_KEY) || '[]'
    ) as LogData[]
    existingLogs.push(logData)
    localStorage.setItem(OFFLINE_LOGS_KEY, JSON.stringify(existingLogs))
}

export const flushOfflineLogs = async () => {
    const storedLogs = JSON.parse(
        localStorage.getItem(OFFLINE_LOGS_KEY) || '[]'
    ) as LogData[]
    if (!storedLogs.length) return

    const online = await isFirebaseOnline()
    if (!online) {
        console.warn('Still offline, cannot flush logs yet.')
        return
    }

    const failedLogs: LogData[] = []

    for (const log of storedLogs) {
        try {
            await addDoc(collection(db, 'logs'), log)
        } catch {
            failedLogs.push(log)
        }
    }

    if (failedLogs.length > 0) {
        localStorage.setItem(OFFLINE_LOGS_KEY, JSON.stringify(failedLogs))
    } else {
        localStorage.removeItem(OFFLINE_LOGS_KEY)
    }
}

window.addEventListener('online', flushOfflineLogs)
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
        flushOfflineLogs()
    }
})
